import React from 'react';
import { AlertTriangleIcon, CalendarClockIcon, Loader2Icon } from 'lucide-react';
import { formatMembershipDate, formatMembershipPrice } from '../../utils/membershipFormat';
import type { MembershipStatus, PractitionerMembership } from '../../types/membership';
interface MembershipSummaryProps {
  membership: PractitionerMembership;
  actionBusy: 'manage' | null;
  actionError: string | null;
  onManage: () => void;
}
const statusLabel: Record<MembershipStatus, string> = {
  active: 'Active',
  complimentary: 'Complimentary period',
  cancel_scheduled: 'Ending soon',
  past_due: 'Payment issue',
  cancelled: 'Ended'
};
const statusTone: Record<MembershipStatus, string> = {
  active: 'bg-purple-100 text-purple-800',
  complimentary: 'bg-purple-100 text-purple-800',
  cancel_scheduled: 'bg-charcoal/[0.07] text-charcoal',
  past_due: 'bg-red-50 text-red-800',
  cancelled: 'bg-charcoal/[0.07] text-charcoal/70'
};
const primaryButton = 'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-purple-700 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 ease-out hover:bg-purple-800 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2 focus-visible:ring-offset-white';
export function MembershipSummary({
  membership,
  actionBusy,
  actionError,
  onManage
}: MembershipSummaryProps) {
  const {
    status
  } = membership;
  const price = formatMembershipPrice(membership.monthlyPriceCents, membership.currency);
  const nextBilling = formatMembershipDate(membership.nextBillingDate);
  const freeUntil = formatMembershipDate(membership.complimentaryEndsAt);
  const endsOn = formatMembershipDate(membership.scheduledEndDate);
  const isEnded = status === 'cancelled';
  return <section aria-labelledby="plan-heading" className="rounded-3xl border border-charcoal/10 bg-white shadow-[0_18px_50px_-32px_rgba(44,44,44,0.45)]" data-id="element-382" data-mp="2acyxo5uf60a2">
      {/* Cancellation already scheduled in Stripe */}
      {status === 'cancel_scheduled' && <div className="flex flex-col gap-4 rounded-t-3xl border-b border-charcoal/10 bg-purple-50 px-6 py-5 md:flex-row md:items-center md:justify-between md:px-10" data-id="element-383" data-mp="ja8cc2pcojvf">
          <p className="flex items-start gap-3 text-base text-charcoal" data-id="element-384" data-mp="san0raxm2f35">
            <CalendarClockIcon aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-purple-700" data-id="element-385" data-mp="x73pa68q2vgr" />

            <span data-id="element-386" data-mp="125560kioeip2">
              Your membership is scheduled to end on{' '}
              <strong className="font-semibold" data-id="element-387" data-mp="16yhu8tjg0nha">
                {endsOn ?? 'the end of your current billing period'}
              </strong>
              .
            </span>
          </p>

          <button type="button" onClick={onManage} disabled={actionBusy !== null} className={primaryButton} data-id="element-388" data-mp="1g0hbnmyddtkr">
            {actionBusy === 'manage' && <Loader2Icon aria-hidden="true" className="h-4 w-4 animate-spin" data-id="element-389" data-mp="1uzl9rxg9f8up" />}

            Manage Membership
          </button>
        </div>}

      {/* Payment problem */}
      {status === 'past_due' && <div className="flex items-start gap-3 rounded-t-3xl border-b border-red-100 bg-red-50 px-6 py-5 md:px-10" data-id="element-390" data-mp="1h3hiabwrtelx">
          <AlertTriangleIcon aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-red-700" data-id="element-391" data-mp="2du9x767yx0m4" />

          <p className="text-base text-red-900" data-id="element-392" data-mp="24244tvdt0h6e">
            <strong className="font-semibold" data-id="element-393" data-mp="240t11vkcz9fa">
              We couldn’t process your latest payment.
            </strong>{' '}
            Update your payment method in Manage Membership to keep
            your practitioner membership active.
          </p>
        </div>}

      <div className="grid gap-10 px-6 py-8 md:px-10 md:py-10 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end" data-id="element-394" data-mp="2rqvr664pq5b9">
        <div data-id="element-395" data-mp="2c8cdavhhcuxy">
          <div className="flex flex-wrap items-center gap-3" data-id="element-396" data-mp="2c8m76u5nbm8x">
            <h2 id="plan-heading" className="font-serif text-4xl md:text-5xl text-charcoal leading-none" data-id="element-397" data-mp="2ha1p4dcj1cn3">
              {membership.planName}
            </h2>

            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusTone[status]}`} data-id="element-398" data-mp="27bvnlwumu8ir">
              {statusLabel[status]}
            </span>
          </div>

          {membership.isFoundingMember && <p className="mt-3 font-serif italic text-lg text-purple-800" data-id="element-399" data-mp="301m8coqrs8nu">
              Founding Member
            </p>}

          <dl className="mt-8 grid gap-6 sm:grid-cols-2 lg:max-w-2xl" data-id="element-400" data-mp="2q96mhgccqzxo">
            <div data-id="element-401" data-mp="2q7c1y34zt9u4">
              <dt className="text-sm text-charcoal/55" data-id="element-402" data-mp="3dzj3rhthm90x">
                Monthly price
              </dt>

              <dd className="mt-1 text-lg font-semibold text-charcoal" data-id="element-403" data-mp="3iqj12o6iqt3y">
                {price ? `${price}/mo` : '—'}

                {status === 'complimentary' && price && <span className="block text-sm font-normal text-charcoal/60" data-id="element-404" data-mp="12bxf4hok9lo0">
                    {freeUntil ? `Free until ${freeUntil}` : 'Complimentary for now'}
                  </span>}

                {membership.isFoundingMember && !isEnded && price && status !== 'complimentary' && <span className="block text-sm font-normal text-charcoal/60" data-id="element-405" data-mp="jdbf764s60lo">
                      Founding rate, locked in while active
                    </span>}
              </dd>
            </div>

            <div data-id="element-406" data-mp="ta7q1ii5iyte">
              <dt className="text-sm text-charcoal/55" data-id="element-407" data-mp="t9o6xygrm4ds">
                {status === 'complimentary' ? 'First charge' : isEnded ? 'Ended on' : 'Next billing date'}
              </dt>

              <dd className="mt-1 text-lg font-semibold text-charcoal" data-id="element-408" data-mp="2bu4817j8b8me">
                {isEnded ? endsOn ?? '—' : status === 'cancel_scheduled' ? 'No further charges' : nextBilling ?? '—'}
              </dd>
            </div>
          </dl>
        </div>

        <div className="flex flex-col items-start gap-5 lg:items-end" data-id="element-409" data-mp="2qpk75qyo1upn">
          {isEnded ? <a href="/practitioners/membership" className={primaryButton} data-id="element-410" data-mp="1t464n4qdm3p3">
              Rejoin Fem Entity
            </a> : status !== 'cancel_scheduled' ? <button type="button" onClick={onManage} disabled={actionBusy !== null} className={primaryButton} data-id="element-411" data-mp="22zpvvdtzwe7i">
              {actionBusy === 'manage' && <Loader2Icon aria-hidden="true" className="h-4 w-4 animate-spin" data-id="element-412" data-mp="1aaspa7z5c5ul" />}

              Manage Membership
            </button> : null}
        </div>
      </div>

      {actionError && <p role="alert" className="mx-6 mb-8 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 md:mx-10" data-id="element-413" data-mp="3okwa545npiud">
          {actionError}
        </p>}
    </section>;
}